import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './DescriptionModal.module.css';
import { Movie } from '../../types/movie';

interface DescriptionModalProps {
  /**
   * Controls whether the modal is open
   */
  isOpen: boolean;
  /**
   * Callback function to close the modal
   */
  onClose: () => void;
  /**
   * Movie/drama object to display information for
   */
  movie: Movie | null;
}

/**
 * Description Modal Component
 * 
 * Used to display detailed information about movies/dramas.
 * Can be reused by any module in the application.
 */
const DescriptionModal: React.FC<DescriptionModalProps> = ({ isOpen, onClose, movie }) => {
  // Add keyboard event listener for ESC key to close modal
  useEffect(() => {
    if (!isOpen || !movie) {return;}
    
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    
    // Prevent scrolling when modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, movie]);

  // Don't render anything if no movie data
  if (!movie) {return null;}

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div 
            className={styles.overlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          >
            <motion.div 
              className={styles.modal}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h2>{movie.title}</h2>
                <button 
                  className={styles.closeButton} 
                  onClick={onClose}
                  aria-label="Close"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              <div className={styles.modalContent}>
                <div className={styles.details}>
                  <div className={styles.meta}>
                    <span className={styles.year}>{movie.year}</span>
                    <span className={styles.dot}>•</span>
                    <span className={styles.episodes}>{movie.episodes} Episodes</span>
                    <span className={styles.dot}>•</span>
                    <span className={styles.score}>{movie.score} Score</span>
                  </div>
                  <p className={styles.description}>{movie.description}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DescriptionModal; 